import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../../Account/Register/Register.style";
import {
  AideCareAttestationsData,
  AideCriminalData,
  AideHouseholdData,
  AideIdentityData,
  AideLeasingAckData,
  AideMedicalExpenseData,
  AideResidence,
} from "../../liveInAideState";
import { US_STATES_WITH_DC } from "./AideStatesLived";

// ---------------------------------------------------------------------------
// Screen 9 — Review & Submit. Renders a read-only SUMMARY of every collected
// aide slice (identity, rental history, household, care attestations, medical
// expense, criminal screening, states lived, leasing acknowledgements) and a
// single Submit button. Submit calls onSubmit(), which the container wires to
// the aide submit endpoint mutation (POST .../live-in-aide/:id/submit).
//
// NOTE: cert SIGNING happens later, in-portal, after staff release. This screen
// only submits the questionnaire — it does not sign anything.
// ---------------------------------------------------------------------------

interface Props {
  /** The full set of collected aide slices, read straight off applicationData. */
  defaultValues?: {
    identity?: Partial<AideIdentityData>;
    rentalHistory?: {
      current?: Partial<AideResidence>;
      previous?: Partial<AideResidence>;
    };
    household?: Partial<AideHouseholdData>;
    careAttestations?: Partial<AideCareAttestationsData>;
    medicalExpense?: Partial<AideMedicalExpenseData>;
    criminal?: Partial<AideCriminalData>;
    statesLived?: string[];
    leasingAck?: Partial<AideLeasingAckData>;
  };
  /** Submit the aide sub-flow (container wires this to the aide submit endpoint). */
  onSubmit: () => void | Promise<void>;
  isSaving?: boolean;
}

/** Render a single "label: value" summary row. */
const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      gap: 2,
      py: 0.5,
      flexWrap: "wrap",
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, textAlign: "right", flexShrink: 0 }}
      data-testid="review-value"
    >
      {value}
    </Typography>
  </Box>
);

export const AideReviewSign: React.FC<Props> = ({
  defaultValues = {},
  onSubmit,
  isSaving = false,
}) => {
  const { t } = useTranslation();

  const yn = (v: boolean | null | undefined): string => {
    if (v === true) return t("affordable.application.liveInAide.review.yes");
    if (v === false) return t("affordable.application.liveInAide.review.no");
    return t("affordable.application.liveInAide.review.notAnswered");
  };
  const orDash = (v: string | undefined | null): string => v || "—";

  const {
    identity,
    rentalHistory,
    household,
    careAttestations,
    medicalExpense,
    criminal,
    statesLived,
    leasingAck,
  } = defaultValues;

  const residenceLine = (r?: Partial<AideResidence>): string => {
    if (!r) return "—";
    const parts = [
      r.landlord,
      r.address,
      [r.city, r.state, r.zip].filter(Boolean).join(", "),
    ].filter(Boolean);
    return parts.length ? parts.join(" · ") : "—";
  };

  const statesLabel = (): string => {
    if (!statesLived || statesLived.length === 0)
      return t("affordable.application.liveInAide.review.noneSelected");
    return statesLived
      .map(
        (code) =>
          US_STATES_WITH_DC.find((s) => s.code === code)?.name || code
      )
      .join(", ");
  };

  return (
    <Box>
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.liveInAide.review.title")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("affordable.application.liveInAide.review.subtitle")}
        </Typography>

        {/* Identity */}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          {t("affordable.application.liveInAide.review.sections.identity")}
        </Typography>
        <Row
          label={t("affordable.application.liveInAide.review.labels.dob")}
          value={orDash(identity?.dob)}
        />
        <Row
          label={t("affordable.application.liveInAide.review.labels.ssn")}
          value={
            identity?.noSsn
              ? t("affordable.application.liveInAide.review.noSsn")
              : orDash(identity?.ssn)
          }
        />
        <Row
          label={t(
            "affordable.application.liveInAide.review.labels.residentServed"
          )}
          value={orDash(identity?.residentServedName)}
        />

        <Divider sx={{ my: 1.5 }} />

        {/* Rental history */}
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
          {t("affordable.application.liveInAide.review.sections.rentalHistory")}
        </Typography>
        <Row
          label={t(
            "affordable.application.liveInAide.review.labels.currentResidence"
          )}
          value={residenceLine(rentalHistory?.current)}
        />
        <Row
          label={t(
            "affordable.application.liveInAide.review.labels.previousResidence"
          )}
          value={residenceLine(rentalHistory?.previous)}
        />

        <Divider sx={{ my: 1.5 }} />

        {/* Household */}
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
          {t("affordable.application.liveInAide.review.sections.household")}
        </Typography>
        <Row
          label={t("affordable.application.liveInAide.household.questions.hudAssistance")}
          value={yn(household?.hudAssistance)}
        />
        <Row
          label={t(
            "affordable.application.liveInAide.household.questions.otherFamilyMoving"
          )}
          value={yn(household?.otherFamilyMoving)}
        />
        <Row
          label={t("affordable.application.liveInAide.household.questions.pets")}
          value={yn(household?.pets)}
        />
        <Row
          label={t(
            "affordable.application.liveInAide.household.questions.goodStanding"
          )}
          value={yn(household?.goodStanding)}
        />

        <Divider sx={{ my: 1.5 }} />

        {/* Care attestations */}
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
          {t(
            "affordable.application.liveInAide.review.sections.careAttestations"
          )}
        </Typography>
        <Row
          label={t(
            "affordable.application.liveInAide.careAttestations.questions.essentialCare"
          )}
          value={yn(careAttestations?.essentialCare)}
        />
        <Row
          label={t(
            "affordable.application.liveInAide.careAttestations.questions.obligatedSupport"
          )}
          value={yn(careAttestations?.obligatedSupport)}
        />
        <Row
          label={t(
            "affordable.application.liveInAide.careAttestations.questions.wouldNotLiveExcept"
          )}
          value={yn(careAttestations?.wouldNotLiveExcept)}
        />

        <Divider sx={{ my: 1.5 }} />

        {/* Medical expense */}
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
          {t("affordable.application.liveInAide.review.sections.medicalExpense")}
        </Typography>
        <Row
          label={t("affordable.application.liveInAide.medicalExpense.feeQuestion")}
          value={yn(medicalExpense?.residentPaysFee)}
        />
        {medicalExpense?.residentPaysFee === true && (
          <Row
            label={t(
              "affordable.application.liveInAide.medicalExpense.amountLabel"
            )}
            value={`${orDash(medicalExpense?.amount)}${
              medicalExpense?.frequency
                ? ` · ${t(
                    `affordable.application.liveInAide.medicalExpense.frequencies.${medicalExpense.frequency}`
                  )}`
                : ""
            }`}
          />
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Criminal screening */}
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
          {t(
            "affordable.application.liveInAide.review.sections.criminalScreening"
          )}
        </Typography>
        <Row
          label={t(
            "affordable.application.liveInAide.criminalScreening.questions.legalActions10y"
          )}
          value={yn(criminal?.legalActions10y)}
        />
        {criminal?.legalActions10y === true && (
          <Row
            label={t(
              "affordable.application.liveInAide.criminalScreening.explainLabel"
            )}
            value={orDash(criminal?.legalActionsExplain)}
          />
        )}
        <Row
          label={t(
            "affordable.application.liveInAide.criminalScreening.questions.sexOffenderLifetime"
          )}
          value={yn(criminal?.sexOffenderLifetime)}
        />
        <Row
          label={t(
            "affordable.application.liveInAide.criminalScreening.questions.sexOffenderOther"
          )}
          value={yn(criminal?.sexOffenderOther)}
        />
        <Row
          label={t(
            "affordable.application.liveInAide.criminalScreening.questions.marijuana"
          )}
          value={yn(criminal?.marijuana)}
        />
        <Row
          label={t(
            "affordable.application.liveInAide.criminalScreening.questions.alcoholAbuse"
          )}
          value={yn(criminal?.alcoholAbuse)}
        />

        <Divider sx={{ my: 1.5 }} />

        {/* States lived */}
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
          {t("affordable.application.liveInAide.review.sections.statesLived")}
        </Typography>
        <Row
          label={t("affordable.application.liveInAide.statesLived.title")}
          value={statesLabel()}
        />

        <Divider sx={{ my: 1.5 }} />

        {/* Leasing acknowledgements */}
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
          {t("affordable.application.liveInAide.review.sections.leasingAck")}
        </Typography>
        <Row
          label={t(
            "affordable.application.liveInAide.leasingAck.questions.readLease"
          )}
          value={yn(leasingAck?.readLease)}
        />
        <Row
          label={t(
            "affordable.application.liveInAide.leasingAck.questions.agreeAbide"
          )}
          value={yn(leasingAck?.agreeAbide)}
        />
        <Row
          label={t(
            "affordable.application.liveInAide.leasingAck.questions.understandEviction"
          )}
          value={yn(leasingAck?.understandEviction)}
        />
        <Row
          label={t(
            "affordable.application.liveInAide.leasingAck.questions.noOccupancyRights"
          )}
          value={yn(leasingAck?.noOccupancyRights)}
        />
        <Row
          label={t(
            "affordable.application.liveInAide.leasingAck.questions.vacateIfAbsent"
          )}
          value={yn(leasingAck?.vacateIfAbsent)}
        />
        <Row
          label={t(
            "affordable.application.liveInAide.leasingAck.questions.vacateOnDeath"
          )}
          value={yn(leasingAck?.vacateOnDeath)}
        />
      </RegisterReviewSection>

      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        sx={{ mt: 2 }}
      >
        {t("affordable.application.liveInAide.review.signLater")}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 4 }}>
        <Button
          variant="contained"
          type="button"
          disabled={isSaving}
          onClick={() => onSubmit()}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("affordable.application.liveInAide.review.submit")}
        </Button>
      </Box>
    </Box>
  );
};
